import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, Lock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { contentEngineApi } from '../../services/contentEngineApi';
import { READER_DOC_OPTIONS, saveReaderPrefill } from './readerPrefill';

// payload: { source: 'ncert'|'gutenberg', ... } ; chapterNumber: chapter to pre-select
export function CreateFromChapterMenu({ payload, chapterNumber, curriculum = {}, disabled }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    contentEngineApi.getAccess()
      .then((a) => setAllowed(Boolean(a?.featureFlags?.readerGenerate)))
      .catch(() => setAllowed(false));
  }, []);

  const goUpgrade = () => navigate('/teacher-assignments?upgrade=pro&reason=reader-generate');

  const pick = async (opt) => {
    if (!allowed) {
      toast.info('Creating documents from books is available on Pro and Elite.');
      goUpgrade();
      return;
    }
    setBusy(true);
    try {
      const book = await contentEngineApi.readerImport(payload);
      saveReaderPrefill({ ...curriculum, generator: opt.generator, kind: opt.id, taskType: opt.taskType, docType: opt.docType, book, chapterNumber });
      navigate('/teacher-assignments');
    } catch (err) {
      if (err.code === 'UPGRADE_REQUIRED') {
        toast.info('Creating documents from books is available on Pro and Elite.');
        goUpgrade();
      } else {
        toast.error(err.message || 'Could not prepare this chapter.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="h-7 text-xs gap-1 bg-ae-blue hover:bg-ae-blue/90 text-white" disabled={disabled || busy || allowed === null} data-testid="reader-create-from-chapter">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : allowed === false ? <Lock className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
          {busy ? 'Preparing…' : 'Create from this chapter'}
          <ChevronDown className="w-3 h-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="reader-create-menu">
        <DropdownMenuLabel className="text-xs">
          {allowed === false ? 'Pro & Elite feature' : 'Open in the generator'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {READER_DOC_OPTIONS.map((opt) => (
          <DropdownMenuItem key={opt.id} onSelect={() => pick(opt)} className="text-sm" data-testid={`reader-create-${opt.id}`}>
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
